/**
 * The board write's transport: the endpoint, the request it sends, the response it decodes,
 * and the two kinds of error it tells apart.
 *
 * Shared by every issues board adapter rather than written per adapter, because a cell
 * address is a field plus a bucket key and that mapping is mechanical. What an adapter still
 * owns is whether it offers the move at all, and what it binds into the request.
 *
 * Proto in both directions, which is what the Planning route's list layout and the nearest
 * planning write already use. The result handed back is plain, so `SavedViewBoard` and the
 * optimistic layer above it never see a schema.
 */

import {create, fromJson, type MessageInitShape, toJson} from '@github-ui/dotcom-schema/protobuf'
import {
  type BoardFieldChangeSchema,
  type BoardUpdateRequest,
  BoardUpdateRequestSchema,
  BoardUpdateResponseSchema,
} from '@github-ui/dotcom-schema/types/issues/v1/board_update'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {BoardMoveFailedError, BoardMoveRefusedError} from '../components/board/board-move-errors'
import {NO_VALUE_KEY} from '../components/board/issues/board-payload'
import type {SavedViewsBoardMoveRequest, SavedViewsBoardMoveResult} from '../components/board/types'

const BOARD_UPDATE_PATH = '/_ghui/issues/update'

/** Names this write in the schema-parse metrics, which are tagged the way a route's read is. */
const ROUTE_ID = 'ghuiIssueUpdate'
const QUERY_NAME = 'boardUpdate'

const UNREADABLE_MESSAGE = 'Could not move this item: the server answered about a different item.'
const UNANSWERED_MESSAGE = 'Could not move this item: the server did not report every change.'
const GENERIC_REFUSAL = 'You cannot move this item.'

/** The error body every route in this family renders. Plain JSON rather than proto. */
type BoardUpdateErrorBody = {error?: Record<string, unknown>}

/**
 * The bucket key as the wire spells it: the no-value bucket is an unset member rather than a
 * key, because the server has no option id for "no value".
 *
 * The only place the browser translates a bucket key at all. Everywhere else it passes back
 * the keys the read payload gave it.
 */
function toWireKey(key: string): string | undefined {
  return key === NO_VALUE_KEY ? undefined : key
}

/**
 * One entry per axis whose key actually changed, which is what makes a sideways drag one
 * change and a diagonal drag two. The arrangement is sent whole either way, because the
 * response has to name the cell and the server cannot recover an untouched axis from this.
 */
export function toBoardFieldChanges({
  from,
  to,
  arrangement,
}: Pick<SavedViewsBoardMoveRequest, 'from' | 'to' | 'arrangement'>): Array<
  MessageInitShape<typeof BoardFieldChangeSchema>
> {
  const axes: Array<[string | null, string, string]> = [
    [arrangement.columnField, from.columnKey, to.columnKey],
    [arrangement.rowField, from.rowKey, to.rowKey],
  ]

  return axes.flatMap(([field, fromKey, toKey]) =>
    field == null || fromKey === toKey ? [] : [{field, from: toWireKey(fromKey), to: toWireKey(toKey)}],
  )
}

export function toBoardUpdateRequest({itemId, from, to, arrangement, query}: SavedViewsBoardMoveRequest) {
  return create(BoardUpdateRequestSchema, {
    id: itemId,
    changes: toBoardFieldChanges({from, to, arrangement}),
    arrangement: {groupBy: arrangement.columnField, swimlaneBy: arrangement.rowField ?? undefined},
    q: query,
  })
}

/**
 * Sends one gesture as one request.
 *
 * Never two, whatever the gesture crossed: the server applies both fields in one transaction
 * and two requests can half-succeed, landing the card in a cell nobody dropped it in.
 */
export async function postBoardUpdate(request: SavedViewsBoardMoveRequest): Promise<SavedViewsBoardMoveResult> {
  const message = toBoardUpdateRequest(request)

  let response: Response
  try {
    response = await reactFetchJSON(BOARD_UPDATE_PATH, {
      method: 'POST',
      body: toJson(BoardUpdateRequestSchema, message),
    })
  } catch {
    throw new BoardMoveFailedError()
  }

  if (!response.ok) throw await toBoardMoveError(response)

  return toBoardMoveResult(await readJson(response), message)
}

/**
 * Any success body that does not decode to an answer for this request is a failed write.
 * `fromJson` throws on a schema mismatch when `react_enforce_schema` is on, and otherwise
 * returns the raw body unchecked, so the reads after it can throw too.
 */
function toBoardMoveResult(raw: unknown, message: BoardUpdateRequest): SavedViewsBoardMoveResult {
  try {
    const decoded = fromJson({schema: BoardUpdateResponseSchema, raw, routeId: ROUTE_ID, queryName: QUERY_NAME})
    // A response carrying a different id folds the pending operation onto nothing and the card
    // snaps back with no error anywhere, so an unmatched echo is a failure rather than a move.
    if (decoded.id !== message.id) throw new BoardMoveFailedError(UNREADABLE_MESSAGE)
    // One result per requested change, in request order. A response answering fewer would
    // settle some of a gesture and leave the rest of it to drop, which is the half-move the
    // single transaction exists to prevent.
    if (decoded.results.length !== message.changes.length) throw new BoardMoveFailedError(UNANSWERED_MESSAGE)

    return {
      itemId: decoded.id,
      results: decoded.results.map(result => ({
        field: result.field,
        keys: result.keys,
        added: result.added,
        removed: result.removed,
        sourceMatched: result.sourceMatched,
      })),
      cells: decoded.cells.map(cell => ({columnKey: cell.columnKey, rowKey: cell.rowKey})),
      changedFieldMatchesView: decoded.changedFieldMatchesView,
    }
  } catch (error) {
    throw error instanceof BoardMoveFailedError ? error : new BoardMoveFailedError()
  }
}

/** A success whose body will not parse is a failed write, not a write with no answer. */
async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    throw new BoardMoveFailedError()
  }
}

/**
 * A refusal is never rolled up into a failure: a refusal is visible and carries a reason, a
 * failure is a retry, and turning the first into the second loses the reason.
 */
async function toBoardMoveError(response: Response): Promise<Error> {
  if (response.status !== 403 && response.status !== 422) return new BoardMoveFailedError()

  try {
    const body = (await response.json()) as BoardUpdateErrorBody
    const error = body?.error
    const {message, field, code, ...details} = typeof error === 'object' && error !== null ? error : {}
    if (typeof message === 'string' && message !== '') {
      return new BoardMoveRefusedError(message, typeof field === 'string' ? field : undefined, {
        code: typeof code === 'string' ? code : undefined,
        details,
      })
    }
  } catch {
    // A refusal whose body did not parse still refused. Fall through to the generic wording
    // rather than reporting a retryable failure the server has already declined.
  }

  return new BoardMoveRefusedError(GENERIC_REFUSAL)
}
